import { useEffect, useRef, useState } from 'react';
import { YouTubeEvent, YouTubePlayer } from 'react-youtube';
import useUserContext from './useUserContext';

const useSyncPlayback = (watchPartyId: string, host: string, currentVideoUrl: string) => {
  const { user, socket } = useUserContext();
  const playerRef = useRef<YouTubePlayer>(null);
  const [currentTime, setCurrentTime] = useState(playerRef.current?.getCurrentTime() || 0);

  const handlePlay = (event: YouTubeEvent) => {
    if (user.username !== host) {
      // If the user is not the host, return early and do not allow the action
      return;
    }
    const time = event.target.getCurrentTime();
    setCurrentTime(time);
    socket.emit('play', watchPartyId, time);
  };

  const handlePause = (event: YouTubeEvent) => {
    if (user.username !== host) {
      // If the user is not the host, return early and do not allow the action
      return;
    }
    const time = event.target.getCurrentTime();
    setCurrentTime(time);
    socket.emit('pause', watchPartyId, time);
  };

  const handleSeek = (event: YouTubeEvent) => {
    if (user.username !== host) {
      // If the user is not the host, return early and do not allow the action
      return;
    }
    const time = event.target.getCurrentTime();
    setCurrentTime(time);
    socket.emit('seek', watchPartyId, time);
  };

  // Sync playback with the host
  useEffect(() => {
    const handlePlayUpdate = (time: number) => {
      if (playerRef.current) {
        playerRef.current.seekTo(time);
        playerRef.current.playVideo();
      }
      setCurrentTime(time);
    };

    const handlePauseUpdate = (time: number) => {
      if (playerRef.current) {
        playerRef.current.seekTo(time);
        playerRef.current.pauseVideo();
      }
      setCurrentTime(time);
    };

    const handleSeekUpdate = (time: number) => {
      if (playerRef.current) {
        playerRef.current.seekTo(time);
      }
      setCurrentTime(time);
    };

    const handleSendCurrentTime = (recipientId: string) => {
      if (user.username === host && playerRef.current) {
        const time = playerRef.current.getCurrentTime();
        socket.emit('sendCurrentTime', recipientId, time);
      }
    };

    const handleSync = (time: number) => {
      if (!playerRef.current) return;

      const currentPlayerTime = playerRef.current.getCurrentTime();
      const timeDiff = time - currentPlayerTime;

      // If the time difference is very small, ignore it to prevent micro-adjustments
      if (Math.abs(timeDiff) < 0.5) return;

      // If the difference is between 0.5 and 3 seconds, adjust playback speed instead of seeking
      if (Math.abs(timeDiff) < 3) {
        const playbackRate = timeDiff > 0 ? 1.1 : 0.9; // Slightly speed up or slow down
        playerRef.current.setPlaybackRate(playbackRate);
      } else {
        // If the difference is too large, seek directly
        playerRef.current.seekTo(time, true);
      }

      setCurrentTime(time);
    };

    // Receive sync events from the server
    socket.on('serverPlay', handlePlayUpdate);
    socket.on('serverPause', handlePauseUpdate);
    socket.on('serverSeek', handleSeekUpdate);
    socket.on('requestCurrentTime', handleSendCurrentTime);
    socket.on('syncTime', handleSync);

    return () => {
      socket.off('serverPlay', handlePlayUpdate);
      socket.off('serverPause', handlePauseUpdate);
      socket.off('serverSeek', handleSeekUpdate);
      socket.off('requestCurrentTime', handleSendCurrentTime);
      socket.off('syncTime', handleSync);
    };
  }, [currentTime, host, socket, user.username]);

  return { playerRef, handlePlay, handlePause, handleSeek };
};

export default useSyncPlayback;
